import type { Duration, Effect, Scope, Stream } from 'effect'

export type Topic = string & { readonly Topic: unique symbol }

export function topic(value: string): Topic {
  return value as Topic
}

export type ConnectionState =
  | {
      readonly _tag: 'disconnected'
      readonly userInitiated: boolean
      readonly lastCloseCode?: number
      readonly lastCloseReason?: string
    }
  | {
      readonly _tag: 'connecting'
      readonly attempt: number
    }
  | {
      readonly _tag: 'connected'
    }
  | {
      readonly _tag: 'reconnecting'
      readonly attempt: number
      readonly nextDelayMs: number
    }

export type Envelope = {
  readonly topic: Topic
  readonly event: string
  readonly payload: unknown
  readonly joinRef: string | null
  readonly ref: string | null
}

export type ConnectParams = Readonly<Record<string, string>>

export type RealtimeVisibility = 'visible' | 'hidden'

export type RealtimeLifecycleEvent =
  | {
      readonly _tag: 'VisibilityChanged'
      readonly visibility: RealtimeVisibility
      readonly time: number
    }
  | {
      readonly _tag: 'PageHide'
      readonly visibility: RealtimeVisibility
      readonly time: number
      // True when browser is entering BFCache.
      readonly persisted: boolean
    }
  | {
      readonly _tag: 'PageShow'
      readonly visibility: RealtimeVisibility
      readonly time: number
      // True when browser restored this page from BFCache.
      readonly persisted: boolean
    }

export type RealtimeLifecycle = {
  readonly currentVisibility: Effect.Effect<RealtimeVisibility>
  readonly events: Stream.Stream<RealtimeLifecycleEvent>
}

export type CloseInfo = {
  readonly code: number
  readonly reason?: string
  readonly wasClean: boolean
}

export type RealtimeSocketConfig = {
  readonly endpoint: string
  readonly getConnectParams: Effect.Effect<ConnectParams, ConnectParamsError>
  /**
   * Optional browser lifecycle feed (visibility + page transitions).
   * If provided, reconnects are paused while hidden and resumed on show/visible.
   */
  readonly lifecycle?: RealtimeLifecycle
  readonly reconnectBaseDelayMs?: number
  readonly reconnectMaxDelayMs?: number
  readonly reconnectJitter?: number
  /**
   * How long to wait for a connection attempt to emit `onOpen` before retrying.
   * Defaults to 15s.
   */
  readonly connectOpenTimeoutMs?: number
  /**
   * Interval between heartbeats while the socket is connected.
   * Defaults to 30s to match phoenix.js behavior.
   */
  readonly heartbeatInterval?: Duration.Input
  /**
   * How long to wait for heartbeat ack before forcing reconnect.
   * Defaults to `heartbeatInterval`.
   */
  readonly heartbeatTimeout?: Duration.Input
  readonly joinTimeoutMs?: number
  readonly joinRetryBaseDelayMs?: number
  readonly joinRetryMaxDelayMs?: number
  /**
   * Reports a desired topic that has remained unjoined for the configured window.
   * The runtime emits once per missing episode and resets the episode when the topic
   * joins, is no longer desired, or browser visibility resumes.
   */
  readonly topicJoinStall?: {
    readonly timeout: Duration.Input
    readonly onStalled: (event: RealtimeTopicJoinStalledEvent) => Effect.Effect<void>
  }
  readonly makeTransport?: RealtimeTransportFactory
  readonly onSocketErrored?: (event: RealtimeSocketErroredEvent) => Effect.Effect<void>
  /** Reports defects from fibers owned by the socket runtime. */
  readonly onDefect?: (defect: RealtimeDefect) => Effect.Effect<void>
}

export type RealtimeDefect = {
  readonly context: string
  readonly error: unknown
}

export type RealtimeSocketErroredEvent = {
  readonly generation: number
  readonly hasTransport: boolean
  readonly error: {
    readonly tag: 'SocketCloseError' | 'SocketGenericError' | 'UnknownError'
    readonly reason?: string
    readonly message: string
    readonly code?: number
    readonly closeReason?: string
    readonly cause?: string
  }
  readonly connectionState: ConnectionState['_tag']
  readonly desiredConnected: boolean
  readonly visibility: RealtimeVisibility
  readonly desiredTopics: ReadonlyArray<string>
  readonly joinedTopics: ReadonlyArray<string>
}

export type RealtimeTopicJoinStalledEvent = {
  readonly topic: string
  readonly timeoutMs: number
  readonly generation: number
  readonly connectionState: ConnectionState['_tag']
  readonly desiredConnected: boolean
  readonly visibility: RealtimeVisibility
  readonly desiredTopics: ReadonlyArray<string>
  readonly joinedTopics: ReadonlyArray<string>
}

/** A push that got no `ok` reply: the topic was not joined, the socket or topic closed before a reply, or the server replied with an error. */
export class PushError extends Error {
  readonly _tag = 'PushError' as const

  constructor(
    readonly reason: 'not_joined' | 'closed' | 'error' | 'timeout',
    readonly response: unknown
  ) {
    super(`push failed: ${reason}`)
  }
}

export interface RealtimeSocket {
  readonly connect: Effect.Effect<void>
  readonly disconnect: Effect.Effect<void>
  /** Discards a topic's current join and pending pushes, then joins it afresh. */
  readonly rejoin: (topic: Topic) => Effect.Effect<void>
  readonly subscribe: (
    topic: Topic,
    onEvent: (event: Envelope) => Effect.Effect<void>
  ) => Effect.Effect<void, never, Scope.Scope>
  /** Sends an event on a joined topic and resolves with the server's `ok` reply response. */
  readonly push: (topic: Topic, event: string, payload: unknown) => Effect.Effect<unknown, PushError>
  readonly connectionStates: Stream.Stream<ConnectionState>
  readonly connectedTopics: Stream.Stream<ReadonlyArray<Topic>>
}

export type RealtimeTransportHandlers = {
  readonly onOpen: Effect.Effect<void>
  readonly onMessage: (data: string | Uint8Array) => Effect.Effect<void>
  readonly onClose: (close: CloseInfo) => Effect.Effect<void>
  readonly onError: (error: unknown) => Effect.Effect<void>
  readonly onDefect: (context: string, error: unknown) => Effect.Effect<void>
}

export type RealtimeTransport = {
  readonly send: (data: string) => Effect.Effect<void, ConnectTransportError>
  readonly close: (code?: number, reason?: string) => Effect.Effect<void, ConnectTransportError>
}

export type RealtimeTransportFactory = (
  url: string,
  handlers: RealtimeTransportHandlers
) => Effect.Effect<RealtimeTransport, ConnectTransportError, Scope.Scope>

export class ConnectParamsError extends Error {
  readonly _tag = 'ConnectParamsError'

  constructor(
    message: string,
    readonly cause?: unknown
  ) {
    super(message)
  }
}

export class ConnectTransportError extends Error {
  readonly _tag = 'ConnectTransportError'

  constructor(
    message: string,
    readonly cause?: unknown
  ) {
    super(message)
  }
}
